import { useEffect, useState } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import { Box, Container, IconButton, Skeleton } from '@mui/material';
import CustomText from 'components/common/CustomText/CustomText';
import { colors } from 'styles/colors';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import FilterIcon from 'icons/FilterIcon';
import PatientsIcon from 'icons/PatientsIcon';
import { dateToString, toTitleCase, urlToPhotoSize } from 'common/utils';
import ImageCarousel from 'components/modals/ImageCarousel/ImageCarousel';
import { useGetUsers } from 'hooks/useUsers';
import { IPhotoSize } from 'types/types';
import useGlobalStyles from 'styles/globalStyles';
import PersonIcon from '@mui/icons-material/Person';
import UserStatus from 'components/statuses/UserStatus';
import { ListUsersDto } from 'types/dto/ListUsersDto';
import { UsersFiltersDto } from 'types/dto/UsersFiltersDto';
import CustomCellLabel from 'components/tables/AllUsers/CustomCellLabel';
import CustomCellValue from 'components/tables/AllUsers/CustomCellValue';
import UserActionButtons from 'components/tables/ActionButtons/UserActionButtons';
import { centerColumns, tableColumnProps } from 'common/constants';
import ChangeUserRole from '../ActionInputs/ChangeUserRole';
import UsersFilters from '../AllUsers/UsersFilters';
import { useGetTeamInvitationsRequests } from 'hooks/useTeamInvitations';
import SmallImagesList from '../SmallImagesList/SmallImagesList';
import TeamRequestActions from '../ActionButtons/TeamRequestActions';


export default function TeamRequestsTable() {
  const globalStyles = useGlobalStyles();
  const [carouselImages, setCarouselImages] = useState<IPhotoSize[]>([]);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [size, setSize] = useState<number>(10);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [carouselIndex, setCarouselIndex] = useState<number>(0);

  const [filters, setFilters] = useState<UsersFiltersDto>({
    name: '',
    role: '',
    status: '',
  });

  const requestParams: ListUsersDto = {
    page: page + 1,
    size,
  };


  const { data, refetch, isLoading } = useGetTeamInvitationsRequests(requestParams, {
    onSuccess: (data: any) => { },
    onError: () => console.log('error'),
  });


  const columns: GridColDef[] = [
    {
      field: 'fullName',
      ...tableColumnProps,
      renderHeader: () => (
        <CustomCellLabel text="Full name" />
      ),
      renderCell: (params) => {
        return (
          <Box display={'flex'} alignItems={'center'} gap={1}>
            <CustomCellValue text={`${toTitleCase(params?.row?.fullName)}`} />
          </Box >
        )
      }
    },
    {
      field: 'dateOfBirth',
      ...tableColumnProps,
      ...centerColumns as any,
      renderHeader: () => (
        <CustomCellLabel text="Date of birth" />
      ),
      renderCell: (params) => {
        return (
          <Box display={'flex'} alignItems={'center'} gap={1}>
            <CustomCellValue text={`${dateToString(new Date(params?.row?.dateOfBirth))}`} />
          </Box >
        )
      }
    },
    {
      field: 'phone',
      ...tableColumnProps,
      ...centerColumns as any,
      renderHeader: () => (
        <CustomCellLabel text='Phone' />
      ),
      renderCell: (params) => {
        const fullPhoneNumber = params.row.requestedBy.phones[0].fullPhoneNumber;
        return (
          <Box>
            <CustomCellValue text={`+${fullPhoneNumber}`} />
          </Box>
        )
      }
    },
    {
      field: 'docNumber',
      ...tableColumnProps,
      ...centerColumns as any,
      renderHeader: () => (
        <CustomCellLabel text='Doc number' />
      ),
      renderCell: (params) => {
        const docNumber = params.row.docNumber;
        return (
          <Box>
            <CustomCellValue text={docNumber} />
          </Box>
        )
      }
    },
    {
      field: 'address',
      ...tableColumnProps,
      renderHeader: () => (
        <CustomCellLabel text='Address' />
      ),
      renderCell: (params) => {
        const address = params.row.address;
        return (
          <Box>
            <CustomCellValue text={address} />
          </Box>
        )
      }
    },
    {
      field: 'images',
      ...tableColumnProps,
      ...centerColumns as any,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Images" />
      ),
      renderCell: (params) => {
        const images = [urlToPhotoSize(params.row.docFile)];
        return (
          <SmallImagesList
            images={images}
            setCarouselImages={setCarouselImages}
            setInitialIndex={setCarouselIndex}
            setCarouselOpen={setCarouselOpen}
          />
        )
      }
    },
    {
      field: 'actions',
      ...tableColumnProps,
      ...centerColumns as any,
      renderHeader: () => (
        <CustomText
          text="Actions"
          fontSize={15}
          fontWeight={600}
          color={colors.secondary}
        />
      ),
      renderCell: (params) => {
        return (
          <Box onClick={(e) => e.stopPropagation()}>
            <TeamRequestActions
              id={params.row.id}
              refetch={refetch}
            />
          </Box>
        )
      },
    }
  ];

  useEffect(() => {
    if (data?.totalCount !== undefined) {
      setRowCount(data.totalCount);
    }
  }, [data?.totalCount]);


  return (
    <Container disableGutters maxWidth={'xl'}>
      <Paper className={globalStyles.tableContainer} sx={{
        overflowX: 'auto',
        minHeight: 11 * 52 + 56, // 10 rows + header
      }}
      >

        {/* Header */}
        <Box
          display={{ sm: 'block', md: 'flex' }}
          justifyContent={'space-between'}
          gap={2}
          sx={{
            px: 2,
            py: 2.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1.5}>
            <Box
              sx={{
                backgroundColor: colors.topBackground,
                display: 'inline-flex',
                height: 40,
                width: 40,
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '8px',
              }}
            >
              <PatientsIcon color={colors.primary} />
            </Box>
            <CustomText text="All Requests" fontSize={16} color={colors.primary} fontWeight={600} />
          </Box>
          <Box display={'flex'} gap={2}>
            <CustomButton
              text='Filter'
              variant='outlined'
              height={40}
              icon={<FilterIcon />}
              onClick={() => setFiltersOpen(prev => !prev)}
            />
          </Box>
        </Box>

        <UsersFilters
          open={filtersOpen}
          initialValues={filters}
          loading={isLoading}
          onApply={(values) => {
            setFilters(values);
            setPage(0);
            refetch();
          }}
          onReset={() => {
            setFilters({ name: '', role: '', status: '' });
            setPage(0);
            refetch();
          }}
        />

        {/* Data */}
        <DataGrid
          sx={{ minHeight: 11 * 52 + 56 }}
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          disableColumnSorting
          disableRowSelectionOnClick
          onRowSelectionModelChange={(selection) => {
            console.log('Selected row IDs:', selection);
          }}
          rows={data?.data || []}
          columns={columns}
          loading={isLoading}
          slotProps={{
            loadingOverlay: {
              children: Array.from({ length: 10 }).map((_, i) => (
                <Skeleton key={i} height={52} />
              )),
            },
          }}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{
            page: page,
            pageSize: size,
          }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setSize(model.pageSize);
          }}
          pageSizeOptions={[10, 20, 30, 50, 100]}
          onRowClick={(params) => setSelectedId(params.row.id)}
          className={globalStyles.dataGrid}
        />
      </Paper>
      <ImageCarousel
        open={carouselOpen}
        images={carouselImages}
        initialIndex={carouselIndex}
        onClose={() => setCarouselOpen(false)}
      />

      <Box mt={3} />
    </Container>
  );
}
